import { useAction, usePersistentScroll } from '@agro-club/frontend-shared'
import useLangPicker from 'hooks/useLangPicker'
import AuthSelectors from 'modules/domain/auth/selectors'
import RetailerTargetSkuActions from 'modules/domain/target2sku/retailerTarget/duck'
import { useRetailerTargetSkuList } from 'modules/domain/target2sku/retailerTarget/hooks'
import RetailerTargetSkuSelectors from 'modules/domain/target2sku/retailerTarget/selectors'
import { CAPABILITY, PERMISSION, usePermissions } from 'modules/permissions/permissions'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { isDistributor, isProducer } from 'types/entities'
import { CompoundedTableEditable } from 'views/components/CompoundedTable/CompoundedTableEditable/CompoundedTableEditable'
import { RetailerTargetSkuForm } from '../forms/RetailerTargetSkuForm'
import { BolderCell, TertiaryCell, TitleCell, TitleI18nCell } from './common'
import { TextTertiary } from '../styles'
import useCompareStrings from 'hooks/useCompareStrings'

export const RetailerTargetSkuTable: React.VFC = () => {
  const { t } = useTranslation('target2')
  const { pick } = useLangPicker()

  const [progress, data = []] = useRetailerTargetSkuList()

  const total = useSelector(RetailerTargetSkuSelectors.total)
  const page = useSelector(RetailerTargetSkuSelectors.page)
  const pages = useSelector(RetailerTargetSkuSelectors.pages)
  const pageSize = useSelector(RetailerTargetSkuSelectors.pageSize)
  const targetsTotal = useSelector(RetailerTargetSkuSelectors.targetsTotal)
  const farmerOrdersTotal = useSelector(RetailerTargetSkuSelectors.farmerOrdersTotal)
  const retailerOrdersTotal = useSelector(RetailerTargetSkuSelectors.retailerOrdersTotal)
  const previousYear = useSelector(RetailerTargetSkuSelectors.previousYear)

  const listRequested = useAction(RetailerTargetSkuActions.listRequested)
  const { scrollRef } = usePersistentScroll('retailerTargetSkuList')

  const userCompany = useSelector(AuthSelectors.userCompany)
  const role = useSelector(AuthSelectors.role)
  const producer = isProducer(role)
  const distributor = isDistributor(role)
  const hideManufacturerColumn = userCompany
    ? producer
      ? true
      : distributor && userCompany.producers_relations.length === 1
      ? false
      : true
    : false

  const hideRetailerColumn = distributor && !userCompany?.has_branch_company
  const compareStrings = useCompareStrings()

  const columns = useMemo(
    () => [
      {
        Header: t('list.tableHeaders.manufacturer'),
        accessor: 'manufacturer',
        width: 193,
        hidden: hideManufacturerColumn,
        Cell: TitleCell,
        sortType: compareStrings,
      },
      {
        Header: t('list.tableHeaders.territory'),
        accessor: 'territory' as const,
        width: 193,
        Cell: TitleCell,
        sortType: compareStrings,
      },
      {
        Header: t('list.tableHeaders.season'),
        accessor: 'season' as const,
        width: 193,
        Cell: TitleCell,
        sortType: compareStrings,
      },
      {
        Header: t('list.tableHeaders.retailer'),
        accessor: 'retailer',
        width: 213,
        hidden: hideRetailerColumn,
        Cell: TitleCell,
        sortType: compareStrings,
      },
      {
        Header: t('list.tableHeaders.subcategory'),
        accessor: 'subcategory',
        width: 97,
        Cell: TitleI18nCell(pick),
        sortType: compareStrings,
      },
      {
        Header: t('list.tableHeaders.product'),
        accessor: 'product',
        width: 129,
        Cell: TitleI18nCell(pick),
        sortType: compareStrings,
      },
      {
        Header: t('list.tableHeaders.retailerTarget'),
        accessor: 'target_value' as const,
        width: 70,
        headerAlign: 'right',
        cellAlign: 'right',
        Cell: BolderCell,
      },
      {
        Header: t('list.tableHeaders.retailerOrders'),
        accessor: 'retailer_orders_sku' as const,
        width: 70,
        headerAlign: 'right',
        cellAlign: 'right',
      },
      {
        Header: t('list.tableHeaders.farmerOrders'),
        accessor: 'farmer_orders_sku' as const,
        width: 70,
        headerAlign: 'right',
        cellAlign: 'right',
      },
      {
        Header: <TextTertiary>{t('list.tableHeaders.farmerPreviousYear')}</TextTertiary>,
        accessor: 'farmer_orders_sku_previous_year' as const,
        width: 70,
        Cell: TertiaryCell,
        headerAlign: 'right',
        cellAlign: 'right',
      },
    ],
    [t, hideManufacturerColumn, compareStrings, hideRetailerColumn, pick],
  )

  const metaColumns = useMemo(
    () => ({
      retailer_orders_sku: retailerOrdersTotal,
      target_value: targetsTotal,
      farmer_orders_sku: farmerOrdersTotal,
      farmer_orders_sku_previous_year: <TextTertiary>{previousYear}</TextTertiary>,
    }),
    [farmerOrdersTotal, previousYear, retailerOrdersTotal, targetsTotal],
  )

  const canEdit = usePermissions({ capability: CAPABILITY.RETAILER_TARGETS, permission: PERMISSION.CRU })

  return (
    <CompoundedTableEditable
      FormComponent={RetailerTargetSkuForm}
      columns={columns}
      metaColumns={metaColumns}
      data={data}
      ref={scrollRef}
      progress={progress}
      total={total}
      currentPage={page}
      pageSize={pageSize}
      onSetPage={(page) => listRequested({ page })}
      pages={pages}
      readonly={!canEdit}
      newTitle={t('form.new')}
      isSortable
      defaultSortField="retailer"
    />
  )
}

export default RetailerTargetSkuTable
